<template>
    <!-- A reusable action bar fixed to the bottom of the screen: a message on the
         left and one or two actions on the right. Drop it onto any page or drawer
         that needs a persistent "here's the state, here's what to do" footer (save
         bars, run buttons, etc.). The action buttons go in the #actions slot, the
         message in #message (or the `message` prop).

         `variant` picks the horizontal anchor:
           - 'page'  (default): spans the content area and clears the left app
                     sidebar from lg up (lg:left-64), matching the System Settings
                     save footer. For normal in-page use.
           - 'panel': pins to a right-hand slide-over -- full width on a mobile
                     bottom sheet, capped to the drawer width and right-aligned from
                     sm up. For use inside SlideOverPanel.

         The bar is fixed, so it spans to the viewport edge -- but the scrolling
         content behind it is inset by the scroll container's scrollbar. Left
         alone, the bar (and any max-width-capped inner row) would sit ~15px wider
         than the content. So the component measures its own scroll container's
         scrollbar width and insets its right edge by that much (scrollbarStyle), so
         its right edge lines up with the content. Pages should still reserve bottom
         space on their scrolling content (e.g. a pb-24 on the body) so the last
         content clears the bar. -->
    <div    ref="bar"
            :style="scrollbarStyle"
            :class="[
                'fixed bottom-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur',
                anchorClass,
            ]">
        <div    class="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
                :class="innerWidthClass"
                :style="innerAlignStyle">
            <!-- Message. From the #message slot, else the `message` prop. Muted by
                 default; callers can render richer content (icons, counts, colour)
                 via the slot. min-w-0 keeps it from pushing the actions off-screen. -->
            <div class="min-w-0 text-sm text-gray-500">
                <slot name="message">{{ message }}</slot>
            </div>

            <!-- Actions, pinned right. The caller drops in one or two buttons (or
                 house button components). On a phone they take the full width and
                 share the row (each flex-1); from sm up they sit at their natural
                 width, right-aligned. Empty -> nothing renders. -->
            <div    v-if="$slots.actions"
                    class="flex shrink-0 gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
                <slot name="actions"></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ActionFooter",
    props: {
        // Left-hand message. Ignored when a #message slot is provided.
        message: {
            type: String,
            default: '',
        },
        // Horizontal anchor: 'page' (content area, clears sidebar) or 'panel'
        // (right drawer width). See the template comment.
        variant: {
            type: String,
            default: 'page',
            validator: (value) => ['page', 'panel'].includes(value),
        },
        // Optional cap on the inner row so it lines up with a capped content column
        // (e.g. pass 'max-w-7xl' so the actions align with a max-w-7xl page body
        // rather than stretching the whole bar). A Tailwind max-w-* class; empty
        // means the row fills the bar. The row is centred (mx-auto) with matching
        // sm:px-6 lg:px-8 gutters when this is set, to mirror a standard content
        // container. Not applied to the 'panel' variant, which is drawer-bounded.
        maxWidthClass: {
            type: String,
            default: '',
        },
        // CSS selector for the content column this footer should line up with. When
        // set, the inner row is positioned to that element's measured left/right
        // edges instead of being capped by maxWidthClass.
        //
        // Use this when the content width cannot be restated as a max-w-* class --
        // e.g. a percentage column (2xl:w-3/4) nested in a gutter-padded container,
        // where the real edges depend on the sidebar offset and the breakpoint.
        // Re-measured on mount, on window resize, and whenever the target itself
        // changes size (ResizeObserver). Ignored by the 'panel' variant.
        alignTo: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            // Width (px) of the scroll container's vertical scrollbar. The fixed bar
            // ignores it, the scrolling content does not, so the bar is inset by
            // this on the right to line their edges up. Measured on mount + resize.
            scrollbarWidth: 0,
            // Measured left/right insets (px) of the `alignTo` target, relative to
            // the bar. Null until measured, or when no target is configured.
            alignInsets: null,
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.measure()
            this.observeAlignTarget()
            this.settleScrollbar()
        })
        window.addEventListener('resize', this.onResize)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize)
        this._alignObserver?.disconnect()
        cancelAnimationFrame(this._settleFrame)
    },
    methods: {
        // Debounced so a burst of resize events settles to one measurement.
        onResize() {
            clearTimeout(this._resizeTimer)
            this._resizeTimer = setTimeout(() => this.measure(), 150)
        },
        measure() {
            this.measureScrollbar()
            this.measureAlignTarget()
        },
        // A page whose rows arrive by AJAX has no scrollbar at mount and gains one a few frames
        // later. That transition resizes no element -- the scroll container's border box is
        // unchanged, only its clientWidth shrinks -- so neither ResizeObserver nor the resize
        // listener sees it. A couple of follow-up frames pick up the scrollbar once it appears.
        settleScrollbar() {
            let frames = 0
            const tick = () => {
                this.measure()

                if (++frames < 3) {
                    this._settleFrame = requestAnimationFrame(tick)
                }
            }

            this._settleFrame = requestAnimationFrame(tick)
        },
        // The aligned column can change width without the window resizing -- content loading in
        // brings up the scroll container's scrollbar, and the app sidebar collapses and expands.
        // Watching the element itself catches those; a resize listener alone leaves the footer
        // pinned to stale edges until the next window resize.
        observeAlignTarget() {
            if (this.variant === 'panel' || ! this.alignTo || typeof ResizeObserver === 'undefined') {
                return
            }

            const target = document.querySelector(this.alignTo)

            if (! target) {
                return
            }

            // Full measure, not just the target: the same layout change that resizes the column
            // (content loading in) is what brings the scrollbar up, and the two insets are
            // computed together.
            this._alignObserver = new ResizeObserver(() => this.measure())
            this._alignObserver.observe(target)

        },
        // Record how far the `alignTo` column's edges sit inside the bar's own, so
        // the inner row can be padded to match. Measured rather than derived: the
        // column's width comes from a percentage plus container gutters, which no
        // single utility class on this row could restate.
        measureAlignTarget() {
            if (this.variant === 'panel' || ! this.alignTo || ! this.$refs.bar) {
                this.alignInsets = null

                return
            }

            const target = document.querySelector(this.alignTo)

            if (! target) {
                this.alignInsets = null

                return
            }

            const targetBox = target.getBoundingClientRect()

            // A hidden or unlaid-out target measures zero; keep the default layout
            // rather than collapsing the row onto one edge.
            if (targetBox.width === 0) {
                this.alignInsets = null

                return
            }

            // Measured against the bar's *anchor* rather than its live box. The bar's own right
            // edge is moved by `scrollbarStyle`, which this measurement then feeds -- reading the
            // live box makes each pass react to the previous one's output, and the insets
            // oscillate by a scrollbar width on every re-measure instead of settling.
            //
            // The anchor is fixed geometry: the bar spans from the sidebar (lg:left-64, or 0
            // below lg) to the viewport's right edge, so it can be derived without measuring.
            // `clientWidth` excludes the scrollbar, which is exactly where `scrollbarStyle` puts
            // the bar's right edge -- so the two agree and neither is double-counted.
            const barLeft = this.$refs.bar.offsetLeft
            const barRight = document.documentElement.clientWidth - this.scrollbarWidth

            this.alignInsets = {
                left: Math.max(0, Math.round(targetBox.left - barLeft)),
                right: Math.max(0, Math.round(barRight - targetBox.right)),
            }
        },
        // Find the bar's nearest scrolling ancestor and record its scrollbar width.
        measureScrollbar() {
            let node = this.$refs.bar ? this.$refs.bar.parentElement : null

            while (node) {
                const overflowY = window.getComputedStyle(node).overflowY

                if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
                    this.scrollbarWidth = node.offsetWidth - node.clientWidth
                    return
                }

                node = node.parentElement
            }

            this.scrollbarWidth = 0
        },
    },
    computed: {
        // Inset the bar's right edge by the scroll container's scrollbar so it ends
        // where the scrolling content does. Only the content-spanning variants need
        // it; the 'panel' variant is bounded to the drawer (sm:right-0) whose own
        // scroll area is separate.
        scrollbarStyle() {
            if (this.variant === 'panel' || this.scrollbarWidth <= 0) {
                return {}
            }

            return {right: `${this.scrollbarWidth}px`}
        },
        // Positioning for the fixed bar. 'page' clears the app sidebar from lg up;
        // 'panel' pins to the right and caps to the drawer width (full width below
        // sm for the mobile bottom sheet).
        anchorClass() {
            if (this.variant === 'panel') {
                return 'inset-x-0 sm:left-auto sm:right-0 sm:w-screen sm:max-w-2xl'
            }

            return 'inset-x-0 lg:left-64'
        },
        // The inner row's width. The panel variant fills the drawer. Otherwise, a
        // caller-supplied maxWidthClass caps and centres the row (with the standard
        // container gutters) so it aligns with a matching content column; with no
        // cap it fills the bar.
        innerWidthClass() {
            if (this.variant === 'panel') {
                return 'mx-auto w-full'
            }

            // When aligning to a measured column, the horizontal padding comes from
            // innerAlignStyle instead -- the utility gutters would stack on top of
            // it and push the actions back inside the content's edge.
            if (this.alignInsets) {
                return 'w-full'
            }

            if (this.maxWidthClass) {
                return `mx-auto w-full ${this.maxWidthClass} lg:px-8`
            }

            return 'w-full'
        },
        // Pads the inner row in to the aligned column's edges. Overrides the row's
        // own px-* utilities via inline style, which is why innerWidthClass drops
        // them in this mode.
        innerAlignStyle() {
            if (! this.alignInsets) {
                return {}
            }

            return {
                paddingLeft: `${this.alignInsets.left}px`,
                paddingRight: `${this.alignInsets.right}px`,
            }
        },
    },
}
</script>
